// HelpPage — /help index.
//
// Fetches the topic summary list, applies a client-side fuzzy filter,
// and renders a grid of TopicCards. Empty / loading / error states
// follow spec §empty/loading/error: skeleton grid on first paint,
// content-sync placeholder when 0 topics return, error panel on
// 5xx.

import { useEffect, useMemo, useState } from 'react'
import { BookOpen } from 'lucide-react'
import { useOperator } from '@/contexts/OperatorContext'

import { listTopics } from './api'
import type { TopicSummary } from './types'
import { TopicCard } from './TopicCard'
import { HelpSearchBar } from './HelpSearchBar'

type LoadState =
  | { kind: 'loading' }
  | { kind: 'ready'; topics: TopicSummary[] }
  | { kind: 'disabled' }
  | { kind: 'error'; message: string }

export function HelpPage() {
  const { short: brandName } = useOperator()
  const [state, setState] = useState<LoadState>({ kind: 'loading' })
  const [query, setQuery] = useState('')

  useEffect(() => {
    let cancelled = false
    listTopics()
      .then((env) => {
        if (cancelled) return
        setState({ kind: 'ready', topics: env.topics })
      })
      .catch((err: Error & { status?: number }) => {
        if (cancelled) return
        if (err.status === 404) {
          setState({ kind: 'disabled' })
          return
        }
        setState({ kind: 'error', message: err.message })
      })
    return () => {
      cancelled = true
    }
  }, [])

  const filtered = useMemo(() => {
    if (state.kind !== 'ready') return []
    const q = query.trim().toLowerCase()
    if (!q) return state.topics
    return state.topics.filter((t) => {
      const hay = [t.title, t.summary, t.slug, t.category].join(' ').toLowerCase()
      return hay.includes(q)
    })
  }, [state, query])

  return (
    <div
      data-testid="help-page"
      className="mx-auto flex w-full max-w-6xl flex-col gap-6 px-4 py-8 sm:px-6 lg:px-8"
    >
      <header className="flex flex-col gap-3">
        <div className="flex items-center gap-2 text-xs font-semibold uppercase tracking-[0.12em] text-butler-600 dark:text-butler-300">
          <BookOpen aria-hidden="true" className="h-4 w-4" />
          {brandName} Help
        </div>
        <h1 className="text-2xl font-display font-semibold text-ink dark:text-slate-100 sm:text-3xl">
          MagicMoney portal walkthroughs
        </h1>
        <p className="max-w-2xl text-sm text-ink-soft dark:text-slate-400">
          Step-by-step instructions for the tasks you run in bl181 — with screenshots,
          deep-links into the portal, and a chat handoff on every step.
        </p>
        <HelpSearchBar value={query} onChange={setQuery} />
      </header>

      {state.kind === 'loading' && <SkeletonGrid />}
      {state.kind === 'disabled' && (
        <EmptyPanel
          title="Help content is loading"
          body="Content-tree sync hasn't run yet. Try again in a minute."
        />
      )}
      {state.kind === 'error' && (
        <EmptyPanel
          title="We couldn't load the help topics"
          body={state.message}
          tone="error"
        />
      )}
      {state.kind === 'ready' && filtered.length === 0 && query.trim() === '' && (
        <EmptyPanel
          title="Help content is loading"
          body="Content-tree sync hasn't run yet. Try again in a minute."
        />
      )}
      {state.kind === 'ready' && filtered.length === 0 && query.trim() !== '' && (
        <EmptyPanel
          title="No topics match your search"
          body={`Nothing here matches "${query}". Try a different keyword, or ask ${brandName} in chat.`}
        />
      )}
      {state.kind === 'ready' && filtered.length > 0 && (
        <section
          aria-label="Help topic list"
          className="grid gap-4 sm:grid-cols-2 lg:grid-cols-3"
          data-testid="help-topic-grid"
        >
          {filtered.map((t) => (
            <TopicCard key={t.slug} topic={t} />
          ))}
        </section>
      )}
    </div>
  )
}

function SkeletonGrid() {
  return (
    <div
      aria-hidden="true"
      data-testid="help-skeleton-grid"
      className="grid gap-4 sm:grid-cols-2 lg:grid-cols-3"
    >
      {Array.from({ length: 8 }).map((_, i) => (
        <div
          key={i}
          className="h-40 animate-pulse rounded-xl border border-hairline bg-surface-raised dark:border-slate-700 dark:bg-slate-900"
        />
      ))}
    </div>
  )
}

function EmptyPanel({
  title,
  body,
  tone = 'info',
}: {
  title: string
  body: string
  tone?: 'info' | 'error'
}) {
  const toneClass =
    tone === 'error'
      ? 'border-rose-300 bg-rose-50 text-rose-900 dark:border-rose-500/40 dark:bg-rose-500/10 dark:text-rose-100'
      : 'border-hairline bg-surface-raised text-ink dark:border-slate-700 dark:bg-slate-900 dark:text-slate-200'
  return (
    <div
      role="status"
      data-testid="help-empty-panel"
      className={`rounded-xl border px-5 py-6 ${toneClass}`}
    >
      <p className="text-sm font-semibold">{title}</p>
      <p className="mt-1 text-sm opacity-80">{body}</p>
    </div>
  )
}
